import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References, FileMeta } from '/imports/types';

export interface CardSet extends Doc {
  _id: string;
  name: string;
  // Optional set-wide goatbots margins, entered as percentages. Applied by the
  // scraper to every card in the set that has no per-card margin of its own:
  // manBuy/SellPrice = gbBuy/SellPrice * (1 + margin/100).
  gbBuyMargin?: number | null;
  gbSellMargin?: number | null;
  // Set symbol/image, scraped from gatherer-static by the gatherer integration.
  image?: FileMeta;
  // --- set-level metadata, parsed from the MTGO CardDataSource client_*.xml
  // <CardSet> roots by importCardXml. `name` above is the short code (e.g. SOS);
  // these enrich it. ---
  // Human-readable expansion name, derived from the set's booster product name
  // (e.g. 'Secrets of Strixhaven Play Booster' -> 'Secrets of Strixhaven').
  fullName?: string;
  // MTGO's monotonic release-order index (older sets lower, newest highest).
  // A reliable sort key for ordering sets newest-first.
  age?: number;
  // Set category: PAPER, MTGO_ONLY, DUEL_DECKS, FROM_THE_VAULT,
  // PREMIUM_DECK_SERIES, PAPER_SPECIAL, or '' (modern paper expansions).
  cardsetType?: string;
  // True for product-only sets whose sole content is a booster and which have no
  // actual cards (e.g. 015, 215, 514, AE0, AK5 — sample/promo booster SKUs).
  isBoosterOnly?: boolean;
  // When the goatbots scrape last wrote this set's regular page. The buy
  // guards read it to decide whether a dear line may be quoted on the price
  // we hold or must wait for a live re-scrape (buyGuards.ts).
  pricedAt?: Date;
}

type Model = CardSet;
type Refs = References<Model>;

export const Sets = new Mongo.Collection<CardSet>('sets');

export const listSets = DeclareMethod<ListMethodArg<CardSet>, ListMethodResponse<CardSet, Refs>>('listSets');
export const getSet = DeclareMethod<string, GetMethodResponse<CardSet, Refs>>('getSet');
export const createSet = DeclareMethod<Partial<CardSet>, string>('createSet');
export const updateSet = DeclareMethod<Partial<CardSet>, string>('updateSet');
export const deleteSet = DeclareMethod<string, undefined>('deleteSet');

export const modelClass: ModelClass<Model, Refs> = {
  collection: Sets,
  editableFields: ['name', 'gbBuyMargin', 'gbSellMargin'],
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return { name: new RegExp(search, 'gi') };
  },
  listMethod: listSets,
  getMethod: getSet,
  createMethod: createSet,
  updateMethod: updateSet,
  deleteMethod: deleteSet,
  pathPrefix: '/sets',
  modelName: 'Set',
  pluralName: 'Sets',
};
